// ----- Phase 5.X-document-handling-enhancements (Feature 2) — Auto-rename affordance -----
//
// Two-step button + modal pattern. The button asks the backend for a
// suggested canonical filename; the modal previews it and offers Apply.
// Apply PATCHes /api/v1/documents/:id with the new FileName. The user
// can Cancel at any point and nothing is mutated.
//
// Per guardrail #2 the backend route is suggestion-only — this component
// owns the entire "apply" UX. We never reuse the suggestion server-side.

import { useCallback, useState, type CSSProperties, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody, v2, type AutoRenameSuggestion, type Document } from '@/api/v2Client';
import { ConfirmModal } from '@/components/shared/ConfirmModal';

export interface AutoRenameAffordanceProps {
  readonly documentId: string;
  readonly currentFileName: string;
  readonly rowVersion?: Document['RowVersion'];
  /** Backend gates this on Documents:write — UI mirrors the gate so the
   *  button doesn't tease users who lack permission. */
  readonly canEdit: boolean;
  /** Called when the user successfully applies the new name. Parent
   *  reloads its doc list. */
  readonly onRenamed: (newFileName: string) => void;
  /** Called on any user-visible error so the parent can route the
   *  message through its existing PageToast. */
  readonly onError: (message: string) => void;
}

const buttonStyle: CSSProperties = {
  background: 'var(--accent, #2563eb)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
};

const suggestionBlockStyle: CSSProperties = {
  marginTop: 8,
  padding: '8px 12px',
  background: 'var(--surface2, #f3f4f6)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text, #111827)',
};

export function AutoRenameAffordance({
  documentId,
  currentFileName,
  rowVersion,
  canEdit,
  onRenamed,
  onError,
}: AutoRenameAffordanceProps): ReactElement {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<AutoRenameSuggestion | null>(null);
  const [applying, setApplying] = useState(false);
  const [expectedRevision, setExpectedRevision] = useState<Document['RowVersion']>();

  const handleClick = useCallback(async (): Promise<void> => {
    if (!canEdit) return;
    setExpectedRevision(rowVersion);
    setLoading(true);
    try {
      const result = await v2.documents.autoRename(documentId);
      setSuggestion(result);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Auto-rename request failed';
      onError(msg);
    } finally {
      setLoading(false);
    }
  }, [canEdit, documentId, onError, rowVersion]);

  const handleApply = useCallback(async (): Promise<void> => {
    if (!suggestion) return;
    setApplying(true);
    try {
      // PATCH via the generic update route. The backend's
      // ALLOWED_WRITE_FIELDS includes FileName so no special envelope.
      await v2.documents.update(documentId, { FileName: suggestion.suggestedName }, expectedRevision);
      onRenamed(suggestion.suggestedName);
      setSuggestion(null);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Failed to apply rename';
      onError(msg);
    } finally {
      setApplying(false);
    }
  }, [documentId, onRenamed, onError, suggestion, expectedRevision]);

  return (
    <>
      <button
        type="button"
        data-testid="auto-rename-button"
        style={canEdit && !loading ? buttonStyle : buttonDisabledStyle}
        disabled={!canEdit || loading}
        onClick={() => void handleClick()}
        title={
          canEdit
            ? 'Suggest an eCTD-canonical filename based on classification + drug name'
            : 'Requires Documents:write permission'
        }
      >
        {loading ? 'Calculating…' : 'Auto-rename'}
      </button>

      {suggestion && (
        <>
          {/* Inline suggestion preview — the modal handles confirm. */}
          <div data-testid="auto-rename-suggestion" style={suggestionBlockStyle}>
            <div style={{ marginBottom: 4 }}>
              <strong>Current:</strong>{' '}
              <code data-testid="auto-rename-current">{currentFileName}</code>
            </div>
            <div>
              <strong>Suggested:</strong>{' '}
              <code data-testid="auto-rename-suggested">{suggestion.suggestedName}</code>
            </div>
            {!suggestion.changed && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: 'var(--text-muted, #6b7280)',
                  fontStyle: 'italic',
                }}
                data-testid="auto-rename-no-change"
              >
                Filename already matches the canonical convention.
              </div>
            )}
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: 'var(--text-muted, #6b7280)',
              }}
              data-testid="auto-rename-reasoning"
            >
              {suggestion.reasoning}
            </div>
          </div>
          <ConfirmModal
            title="Apply suggested filename?"
            message={
              suggestion.changed
                ? `Rename "${currentFileName}" → "${suggestion.suggestedName}"? This updates the Document.FileName only; the underlying file content is not modified.`
                : `The filename "${currentFileName}" already matches the canonical convention. No change will be applied.`
            }
            confirmLabel={suggestion.changed ? (applying ? 'Applying…' : 'Apply') : 'Close'}
            variant="default"
            onCancel={() => setSuggestion(null)}
            onConfirm={() => (suggestion.changed ? void handleApply() : setSuggestion(null))}
          />
        </>
      )}
    </>
  );
}
